import { Button, Modal } from 'antd';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { DUTY_ANALYTICS_COPY } from '../copy';
import type { FigureInfoContent } from '../figureInfo';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C.1 §3] ONE CARD'S OR SECTION'S "WHAT THIS SHOWS" — an info button beside its heading, and a short dialog.
 *
 *  ⚠️ A CONTROLLED `Modal`, NEVER `modal.info`. That hook's holder renders above the router and the query client
 *  (`App.tsx`), the overlay census cannot see it, and its defaults — no close button, no mask close, 416 pixels —
 *  would put a seven-note table's dialog past the 768-pixel floor. The precedent is the arrangement builder's
 *  "What is an arrangement?" dialog. `destroyOnHidden`, so a closed one mounts nothing; ONE census site
 *  (`FigureInfo.tsx#Modal@0`) however many render, swept by `e2e/fixtures/overlays.ts`.
 *
 *  ⚠️ THE BODY IS THE SIBLING DIALOGS' BOUNDED SCROLLER, AND ONE COLUMN. A portalled dialog has no `@container`
 *  ancestor, so the notes card's `@min-` grid would never apply here; a single column needs no breakpoint.
 *
 *  ⚠️ EVERY WORD IS `DUTY_ANALYTICS_COPY`'s — the same sentences the hidden notes card holds — so nothing is
 *  reworded on its way into a dialog. The button's name starts with "What" and carries the title it explains.
 *
 *  ⚠️ THE BODY'S TEST ID CARRIES THE KEY, BECAUSE A TEST CANNOT TELL TWO ANTD DIALOGS APART BY NAME — MEASURED:
 *  rc-util's `useId` answers the constant `test-id` under `NODE_ENV=test`, so every Modal's `aria-labelledby`
 *  points at the FIRST title in the document. Production ids are unique; the key is for the test's sake.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = DUTY_ANALYTICS_COPY;

export const FigureInfo = ({
  infoKey,
  title,
  content,
}: {
  /** The test id's suffix: a card kind, or the section's name in capitals. */
  infoKey: string;
  title: string;
  content: FigureInfoContent;
}) => {
  const [open, setOpen] = useState(false);
  const label = copy.infoLabel(title);
  return (
    <>
      <Button
        type="text"
        size="small"
        icon={<Info className="size-4" aria-hidden />}
        aria-label={label}
        data-testid={`duty-analytics-info-${infoKey}`}
        onClick={() => {
          setOpen(true);
        }}
      />
      <Modal
        open={open}
        title={label}
        footer={null}
        width={560}
        centered
        destroyOnHidden
        onCancel={() => {
          setOpen(false);
        }}
      >
        <div
          className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1 text-sm"
          data-testid={`duty-analytics-info-body-${infoKey}`}
        >
          <dl className="flex flex-col gap-2">
            {content.metrics.map((metric) => (
              <div key={metric} className="flex flex-col gap-0.5">
                <dt className="font-medium">{copy.columns[metric]}</dt>
                <dd className="text-muted-foreground">{copy.notes[metric]}</dd>
              </div>
            ))}
          </dl>
          {content.layoutNote && <p className="text-muted-foreground">{copy.layoutNote}</p>}
          <p className="text-muted-foreground">{copy.ordersNote}</p>
        </div>
      </Modal>
    </>
  );
};
